import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';

/**
 * Rotación de la contraseña inicial.
 *
 * El API responde 428 a todo lo demás hasta que esto se completa, así que
 * mientras el cambio sea obligatorio esta pantalla es la única que funciona.
 * Eso **no significa que deba ser un callejón sin salida**: una sesión a medio
 * estrenar dura horas, y quien llegue después a ese navegador —o al enlace— se
 * encontraba con un formulario de contraseña sin forma de volver al login y sin
 * saber de quién era la cuenta. Por eso la pantalla dice a nombre de quién está
 * y ofrece cerrar sesión.
 */
export function ChangePassword() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeated, setRepeated] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mandatory = user?.passwordChangeRequired ?? false;

  async function handleLogout() {
    await logout();
    navigate('/ingresar', { replace: true });
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (newPassword !== repeated) {
      setError('La contraseña nueva y su repetición no coinciden');
      return;
    }
    if (newPassword.length < 12) {
      setError('La contraseña debe tener al menos 12 caracteres');
      return;
    }

    setBusy(true);
    try {
      await changePassword(currentPassword, newPassword);
      navigate(user?.role === 'CLIENT' ? '/portal' : '/admin', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No pudimos cambiar la contraseña. Revisa tu conexión e inténtalo de nuevo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--space-4)' }}>
      <form className="card" onSubmit={handleSubmit} style={{ width: 'min(460px, 100%)' }} noValidate>
        <h1>Cambia tu contraseña</h1>
        {/* A nombre de quién: si la sesión abierta no es la tuya, se ve de
            inmediato y hay cómo salir. */}
        <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--ink-soft)', fontSize: 'var(--text-sm)' }}>
          Sesión iniciada como <strong>{user?.email}</strong>.
        </p>
        {mandatory && (
          <div className="alert alert--info" style={{ marginTop: 'var(--space-4)' }}>
            Por seguridad debes reemplazar la contraseña inicial antes de continuar.
          </div>
        )}

        {error && <div className="alert alert--error" role="alert">{error}</div>}

        <div className="field">
          <label htmlFor="current">Contraseña actual</label>
          <input
            id="current"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="next">Contraseña nueva</label>
          <input
            id="next"
            type="password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            aria-describedby="password-hint"
          />
          <span id="password-hint" style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-soft)' }}>
            Al menos 12 caracteres, combinando letras y números.
          </span>
        </div>

        <div className="field">
          <label htmlFor="repeat">Repite la contraseña nueva</label>
          <input
            id="repeat"
            type="password"
            autoComplete="new-password"
            required
            value={repeated}
            onChange={(event) => setRepeated(event.target.value)}
          />
        </div>

        <button className="button button--primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Guardando…' : 'Guardar contraseña'}
        </button>

        <button
          type="button"
          className="button"
          onClick={handleLogout}
          style={{ width: '100%', marginTop: 'var(--space-2)' }}
        >
          {mandatory ? 'No soy yo, cerrar sesión' : 'Cancelar y cerrar sesión'}
        </button>
      </form>
    </main>
  );
}
